import {
  Gym,
  GymTypes,
  HandledError,
  IEnabledFeatures,
  ILiveStreamOptions,
  NotFoundError,
  ProgramType,
  RemovedReasonType,
  TimeSpan,
} from 'fitbeat.models';
import {
  BookingsConfig, getWorkoutDuration, GymConfigs, homeWorkoutTypes,
  ProgramsConfig,
} from '../../config';
import { WorkoutType } from '../../config/workouttype';
import { UserProfile, WorkoutSettings } from '../../entities/user';
import { UserWorkoutSession } from '../../entities/workoutsession';
import { setBookingsRemovedState } from '../../helpers';
import {
  DbGymRepository, IUserProfileRepository, IWorkoutRepository, WorkoutFilter,
} from '../../persistence';
import { PartnersManager } from '../partners';
import { WorkoutProgramRecommendations } from '../workoutprograms/workoutprogramrecommendations';
import { hasWorkoutInviteExpired } from './bookingsHelper';
import { HomeTimeSlot } from './hometimeslot';

export class HomeBookingsManager {
  public static getAdditionalTimeBeforeStartOfWarmup(
    isHomeGroupWorkout: boolean,
    livestreamOptions?: ILiveStreamOptions,
  ) {
    if (isHomeGroupWorkout && livestreamOptions?.preWorkoutTalk) {
      return BookingsConfig.streamingCallChatTimeBeforeStartOfWarmup;
    }
    return BookingsConfig.homeBookingBufferSecondsBeforeStartOfWarmup;
  }

  public static setStartAndEndTime(
    userWorkoutSession: UserWorkoutSession,
    bookingTime: Date,
    playbackSpeed?: number,
    livestreamOption?: ILiveStreamOptions,
    isHomeGroupWorkout?: boolean,
  ): void {
    // Increasing endtime delays the start of the warmup.
    const additionalSecondsBeforeStartOfWarmup = HomeBookingsManager.getAdditionalTimeBeforeStartOfWarmup(
      !!isHomeGroupWorkout,
      livestreamOption,
    );

    const earliestJoinTime = bookingTime.getTime() - BookingsConfig.allowedStartTimeBeforeFirstExercise * 60000;
    userWorkoutSession.startTime = new Date(earliestJoinTime);
    userWorkoutSession.originalStartTime = userWorkoutSession.startTime;

    const startTimeOfFirstExercise = bookingTime;
    userWorkoutSession.startTimeOfFirstExercise = startTimeOfFirstExercise;
    userWorkoutSession.originalStartTimeOfFirstExercise = startTimeOfFirstExercise;

    const workoutDuration = getWorkoutDuration(userWorkoutSession.workoutType, playbackSpeed).workoutDurationInMinutes;

    const endTime = new Date(
      bookingTime.getTime()
      + (workoutDuration * 60000)
      + (additionalSecondsBeforeStartOfWarmup * 1000),
    );
    userWorkoutSession.endTime = endTime;
    userWorkoutSession.originalEndTime = endTime;
    userWorkoutSession.additionalSecondsBeforeFirstExercise = additionalSecondsBeforeStartOfWarmup;
  }

  constructor(
    private workoutRepository: IWorkoutRepository,
    private userRepository: IUserProfileRepository,
    private partnerManager: PartnersManager,
    private logger: any,
  ) { }

  public async BookSingle(
    user: UserProfile,
    enabledFeatures: IEnabledFeatures,
    startTime: Date,
    workoutType: WorkoutType,
    requestTime: Date,
    gym: Gym,
    isInstant: boolean,
    isDemo: boolean,
    timezoneOffset: number,
    settings: WorkoutSettings,
    playbackSpeed?: number,
    isHomeGroupWorkout?: boolean,
    livestreamOptions?: ILiveStreamOptions,
    isStreamingWorkout?: boolean,
    pairedSessionReference?: string,
    pairedSessionUserProfileId?: string,
  ): Promise<UserWorkoutSession> {
    const gymTypeConfig = GymConfigs[gym.type];

    if (gymTypeConfig.allowedWorkoutTypes.indexOf(workoutType) < 0) {
      throw new HandledError(`Cannot make home booking with workout type ${workoutType}`);
    }

    const result = await Promise.all([
      this.workoutRepository.getUserWorkoutCount(user.uid),
      this.preventConflictsAndOverBooking(user, startTime, requestTime, workoutType),
    ]);
    const isFirstTimeBooking = result[0] === 0;
    const workoutSettings = settings || ProgramsConfig.fallbackSettingsForGoal(user.longTermGoal, user.skillLevel);
    const isInviteBooking = pairedSessionReference && pairedSessionUserProfileId;
    const ambassadorCreatedGroupWorkout = isHomeGroupWorkout && !isInviteBooking && user.isStreamingUser;
    const workoutSession = new UserWorkoutSession(
      user.uid,
      workoutType,
      workoutSettings,
      undefined,
      startTime,
      undefined,
      requestTime,
      gym,
      undefined,
      ambassadorCreatedGroupWorkout ? livestreamOptions : undefined,
    );

    workoutSession.memberMainGym = user.gymName;
    workoutSession.isInstant = isInstant;
    workoutSession.isDemo = isFirstTimeBooking;
    workoutSession.timezoneOffset = timezoneOffset;
    if (isStreamingWorkout) {
      workoutSession.isStreamingWorkout = isStreamingWorkout;
    }

    workoutSession.pairedSessionUserProfileId = pairedSessionUserProfileId;
    workoutSession.pairedSessionReference = pairedSessionReference;

    HomeBookingsManager.setStartAndEndTime(
      workoutSession,
      startTime,
      playbackSpeed,
      livestreamOptions,
      isHomeGroupWorkout,
    );

    await this.workoutRepository.add(workoutSession);
    return workoutSession;
  }

  public async CancelBooking(
    workoutSession: UserWorkoutSession,
    dateOfRequest: Date,
    removedReason: RemovedReasonType,
  ): Promise<void> {
    setBookingsRemovedState(workoutSession, dateOfRequest, removedReason);
    await this.workoutRepository.update(workoutSession);
  }

  public async ChangeBooking(
    userId: string,
    enabledFeatures: IEnabledFeatures,
    workoutSession: UserWorkoutSession,
    changeToStartTime: Date,
    changeToWorkoutType: WorkoutType,
    dateOfRequest: Date,
    gym: Gym,
    workoutSettings?: WorkoutSettings,
  ): Promise<UserWorkoutSession> {
    const user = await this.userRepository.get(userId);

    if (!user) {
      throw new NotFoundError('User', userId);
    }

    const settings = workoutSettings || new WorkoutSettings(workoutSession.settings);

    const timezoneOffset = workoutSession.timezoneOffset ? workoutSession.timezoneOffset : 0;
    await this.CancelBooking(workoutSession, dateOfRequest, RemovedReasonType.Cancelled);
    return this.BookSingle(
      user,
      enabledFeatures,
      changeToStartTime,
      changeToWorkoutType,
      dateOfRequest,
      gym,
      workoutSession.isInstant,
      workoutSession.isDemo,
      timezoneOffset,
      settings,
    );
  }

  public async GetAvailableBookingSlots(
    userId: string,
    from: Date,
    dateOfRequest: Date,
    timezone: string,
  ): Promise<HomeTimeSlot[]> {
    const isToday = TimeSpan.areOnSameDayInTimeZone(from, dateOfRequest, timezone);
    const endOfDay = TimeSpan.createEndOfDayDateInTimezone(from, timezone);
    const todaysBookings = await this.getConfirmedBookingsForUser(userId, from, endOfDay, from);

    const homeTimeSlots: HomeTimeSlot[] = [];
    let fromNextInterval = this.roundToNextInterval(from, BookingsConfig.homeBookingIntervalsInMinutes);

    if (!isToday) {
      fromNextInterval = TimeSpan.getStartOfDayInTimezonePlusHours(
        from,
        timezone,
        BookingsConfig.homeBookingAvailableSlotsStartTimeHour,
      );
    }

    homeWorkoutTypes.forEach((workoutType) => {
      for (let timeIntervalIterator = fromNextInterval.getTime(); timeIntervalIterator <= endOfDay.getTime();
        timeIntervalIterator += TimeSpan.millisecondsForMinutes(BookingsConfig.homeBookingIntervalsInMinutes)) {
        const homeTimeSlot = this.calculateHomeTimeSlot(
          new Date(timeIntervalIterator),
          todaysBookings,
          workoutType,
          dateOfRequest,
        );
        homeTimeSlots.push(homeTimeSlot);
      }
    });
    return homeTimeSlots;
  }

  public async GetAvailableInfluencerBookingSlots(
    userId: string,
  ): Promise<UserWorkoutSession[]> {
    const from = new Date();
    const bookings = await this.workoutRepository.getWorkoutsAfterPointInTime(
      userId,
      from,
    );
    const filteredResults = bookings.filter((singleItem: UserWorkoutSession) => singleItem.openInviteLink);
    return filteredResults;
  }

  public async AcceptInvite(
    userId: string,
    workoutId: string,
    enabledFeatures: IEnabledFeatures,
    requestTime: Date,
    useInvitersSettings: boolean,
  ): Promise<UserWorkoutSession> {
    const user = await this.userRepository.get(userId);
    const inviterWorkout = await this.workoutRepository.getWorkoutSessionFromId(workoutId);

    if (!user) {
      throw new NotFoundError('user', userId);
    }

    if (!inviterWorkout) {
      throw NotFoundError.Workout(workoutId);
    }

    const inviter = await this.userRepository.get(inviterWorkout.userProfileId);
    if (!inviter) {
      throw new NotFoundError('user', inviterWorkout.userProfileId);
    }

    const { isStreamingWorkout } = inviterWorkout;
    const gym = DbGymRepository.getByName(inviterWorkout.gymName);
    const timezoneOffset = inviterWorkout.timezoneOffset ? inviterWorkout.timezoneOffset : 0;
    const workoutReccomendations = new WorkoutProgramRecommendations(
      this.workoutRepository,
      this.userRepository,
      this.logger,
    );
    const settings = await this.getSettingsForWorkout(
      workoutReccomendations,
      inviterWorkout,
      enabledFeatures,
      user,
      useInvitersSettings,
      gym,
    );
    const userWorkoutSession = await this.BookSingle(
      user,
      enabledFeatures,
      inviterWorkout.startTimeOfFirstExercise,
      inviterWorkout.workoutType,
      requestTime,
      gym,
      false,
      inviterWorkout.isDemo,
      timezoneOffset,
      settings,
      undefined,
      true,
      inviterWorkout.livestreamOptions,
      isStreamingWorkout,
      inviterWorkout.uniqueReference,
      inviter.uid,
    );

    userWorkoutSession.pairedSession = inviterWorkout;
    await this.addWorkoutPartners(user, inviter);

    return userWorkoutSession;
  }

  public async getOpenInviteWorkoutError(
    userId: string,
    workoutInvite: UserWorkoutSession,
    dateOfRequest: Date,
  ): Promise<string | undefined> {
    let error: string | undefined;
    try {
      const user = await this.userRepository.get(userId);
      if (!user) {
        throw new NotFoundError('user', userId);
      }

      await this.preventConflictsAndOverBooking(
        user,
        workoutInvite.startTime,
        dateOfRequest,
        workoutInvite.workoutType,
      );

      if (hasWorkoutInviteExpired(workoutInvite, dateOfRequest)) {
        throw new HandledError('The invite for this workout has expired');
      }
    } catch (err) {
      if (err instanceof HandledError) {
        error = err.message;
      } else {
        this.logger.warn(err);
      }
    }
    return error;
  }

  public async resumeHomeWorkout(workoutId: string, pauseStart: Date | null, pauseResume: Date)
    : Promise<UserWorkoutSession> {
    const workout = await this.workoutRepository.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }

    const now = new Date();

    // if start or resume first time make sure to checkIn so workout does not get removedBy flaged in cronjob
    if (!workout.checkedIn) {
      UserWorkoutSession.markSessionAsCheckedIn(
        workout,
        true,
        now,
        workout.workoutSelectionId,
      );
    }

    // workout that is not paused should not change endtime (like group workouts)
    if (pauseStart) {
      if (!workout.homeWorkoutStartedAt) {
        workout.homeWorkoutStartedAt = now;
      }

      const pauseDuration = pauseResume.getTime() - pauseStart.getTime();
      const addPauseDurationToDate = (original: Date): Date => new Date(
        original.setTime(original.getTime() + pauseDuration),
      );

      workout.endTime = addPauseDurationToDate(workout.endTime);
      workout.startTimeOfFirstExercise = addPauseDurationToDate(workout.startTimeOfFirstExercise);
      workout.startTime = addPauseDurationToDate(workout.startTime);
    }
    await this.workoutRepository.update(workout);
    return workout;
  }

  private calculateHomeTimeSlot(
    startTime: Date,
    todaysUsersBookings: UserWorkoutSession[],
    workoutType: WorkoutType,
    requestTime: Date,
  ): HomeTimeSlot {
    const conflictBookings = this.getConflictWorkouts(startTime, todaysUsersBookings, requestTime, workoutType);
    const conflictBooking = conflictBookings.length > 0 ? conflictBookings[0] : null;

    let gymType;
    if (conflictBooking) {
      gymType = DbGymRepository.getByName(conflictBooking.gymName).type;
    }

    const conflictingWorkoutId = conflictBooking ? conflictBooking.uniqueReference : null;
    const homeTimeSlot = new HomeTimeSlot();
    homeTimeSlot.workoutType = workoutType;
    homeTimeSlot.booked = !!conflictBooking && gymType === GymTypes.Home;
    homeTimeSlot.workoutId = homeTimeSlot.booked ? conflictingWorkoutId : null;
    homeTimeSlot.bookedInAnotherGym = !!conflictBooking && gymType !== GymTypes.Home;
    homeTimeSlot.startTime = startTime.getTime();

    return homeTimeSlot;
  }

  private roundToNextInterval(date: Date, interval: number): Date {
    const ms = 1000 * 60 * interval;
    return new Date(Math.ceil(date.getTime() / ms) * ms);
  }

  private async preventConflictsAndOverBooking(
    user: UserProfile,
    startTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
  ): Promise<void> {
    const bookingStartOfDay: Date = TimeSpan.createStartOfDayDate(startTime);
    const endOfDay = TimeSpan.createEndOfDayDate(startTime);
    const todaysBookings = await this.getConfirmedBookingsForUser(user.uid, bookingStartOfDay, endOfDay, requestTime);
    const todaysHomeBookings = todaysBookings.filter((booking) => {
      const gym = DbGymRepository.getByName(booking.gymName);
      return gym.type === GymTypes.Home;
    });

    if (!user.has24HourAccess && this.hasHomeOverbookings(todaysHomeBookings)) {
      throw new HandledError(
        `Cant make more than ${BookingsConfig.maxNumberOfAllowedHomeBookingsForDay} bookings per day`,
      );
    }

    const conflictBookings = this.getConflictWorkouts(startTime, todaysBookings, requestTime, workoutType);
    if (conflictBookings.length > 0) {
      throw new HandledError('This booking conflicts with another booking');
    }
  }

  private hasHomeOverbookings(todaysBookingsForUser: UserWorkoutSession[]): boolean {
    const allowedBookingCount = BookingsConfig.maxNumberOfAllowedHomeBookingsForDay;
    if (todaysBookingsForUser.length >= allowedBookingCount) {
      return true;
    }
    return false;
  }

  private getConflictWorkouts(
    startTime: Date,
    todaysBookingsForUser: UserWorkoutSession[],
    dateOfRequest: Date,
    workoutType: WorkoutType,
  ): UserWorkoutSession[] {
    const workoutDuration = Math.floor(getWorkoutDuration(workoutType).workoutDurationInMinutes);
    const endTime = new Date(startTime.getTime() + (workoutDuration * 60000));

    const filter = (booking: UserWorkoutSession) => ((startTime <= booking.startTime && endTime >= booking.startTime)
        || (startTime >= booking.startTime && startTime <= booking.endTime))
      && booking.endTime.getTime() > dateOfRequest.getTime() && booking.isSameDay(startTime);
    return todaysBookingsForUser.filter(filter);
  }

  private getConfirmedBookingsForUser(userId: string, from: Date = new Date(), to?: Date, dateOfRequest?: Date):
  Promise<UserWorkoutSession[]> {
    return this.workoutRepository.get(
      undefined,
      userId,
      from,
      to,
      WorkoutFilter.FilterRemoved(dateOfRequest),
      from,
    );
  }

  private async addWorkoutPartners(user: UserProfile, inviter: UserProfile): Promise<void> {
    try {
      await Promise.all([
        this.partnerManager.addPartner(inviter, user),
        this.partnerManager.addPartner(user, inviter),
      ]);
    } catch (error) {
      this.logger.warn('Unable to add workout partners', error);
    }
  }

  private async getSettingsForWorkout(
    workoutRecommendations: WorkoutProgramRecommendations,
    inviterWorkout: UserWorkoutSession,
    enabledFeatures:
    IEnabledFeatures,
    user: UserProfile,
    useInvitersSettings: boolean,
    gym: Gym,
  ): Promise<WorkoutSettings> {
    const defaultToAllRounder = user.longTermGoal ?? ProgramType.GetStrongAndFitGoal;
    const recommendedSettings = await workoutRecommendations.GetRecommendedSettingsForDate(
      user.uid,
      inviterWorkout.startTimeOfFirstExercise,
      enabledFeatures,
      gym,
      defaultToAllRounder,
      new Date(),
    );

    if (!recommendedSettings) {
      throw new Error(`No recommendedSettings found for ${user.uid}`);
    }

    if (useInvitersSettings) {
      return new WorkoutSettings({
        ...inviterWorkout.settings,
        ignoreProgramRecommendations: true,
      });
    }

    return new WorkoutSettings(recommendedSettings);
  }
}
